import React from 'react';
import { Button, Typography, Paper, Grid, Box, Divider, Chip, Avatar, useMediaQuery } from '@mui/material';
import { WhatsApp, Phone, CheckCircle, Email, LocationOn } from '@mui/icons-material';
import ImageCarousel from './ImageCarousel';
import './CarDetails.css';

const CarDetails = ({ car }) => {
  const isMobile = useMediaQuery('(max-width:600px)');
  const isTablet = useMediaQuery('(max-width:900px)');

  if (!car || Object.keys(car).length === 0) {
    return (
      <Typography variant="h6" sx={{ p: 4, textAlign: 'center' }}>
        🚗 Огласот не е достапен.
      </Typography>
    );
  }

  const sellerName = car.sellerType === 'dealer'
  ? car.companyName || 'Авто Салон'
  : car.userName || 'Приватен продавач';

const sellerType = car.sellerType === 'dealer' ? 'Авто Салон' : 'Приватен продавач';
  const sellerPhone = car.contactNumber || car.phone || '';
  const whatsappNumber = sellerPhone ? `389${sellerPhone.replace(/\D/g, '').substring(1)}` : '';
  const viberNumber = sellerPhone;

  const features = Object.entries(car.equipment || {})
    .filter(([_, value]) => value)
    .map(([key]) => key);

  const featureLabels = {
    powerSteering: "Серво волан",
    cruiseControl: "Темпомат",
    centralLock: "Централна брава",
    tirePressureSensor: "Сензор за притисок во гуми",
    electricSeats: "Електрични седишта",
    electricMirrors: "Електрични ретровизори",
    climate: "Клима уред",
    electricWindows: "Електрични прозорци",
    airbags: "Airbag систем",
    abs: "ABS систем",
    fogLights: "Магленки",
    esp: "ESP стабилизација",
    navigation: "Навигација",
    steeringWheelControls: "Команди на волан"
  };

  return (
    <div className="car-details-page">
      {/* Google Ads Sidebar Left - Desktop Only */}
      {!isTablet && (
        <Box className="ad-sidebar left">
          <Paper elevation={1} sx={{ p: 2, height: '100%', textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Реклама
            </Typography>
            <Box sx={{ 
              width: '160px', 
              height: '600px', 
              backgroundColor: '#f5f5f5',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto'
            }}>
              Google Ad (160x600)
            </Box>
          </Paper>
        </Box>
      )}

      {/* Main Content */}
      <Box className="car-details-content">
        <Paper className="car-details-container" sx={{ p: isMobile ? 2 : 3 }}>
          {/* Mobile Top Ad Banner */}
          {isMobile && (
            <Box sx={{ mb: 3 }}>
              <Paper elevation={1} sx={{ p: 1, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Реклама
                </Typography>
                <Box sx={{ 
                  width: '100%', 
                  height: '100px', 
                  backgroundColor: '#f5f5f5',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  Google Ad (320x100)
                </Box>
              </Paper>
            </Box>
          )}

          <Grid container spacing={isMobile ? 2 : 4}>
            <Grid item xs={12} md={6}>
              <Box sx={{ position: 'relative' }}>
                {car.isPremium && (
                  <Chip
                    label="Премиум"
                    color="primary"
                    sx={{ 
                      position: 'absolute', 
                      top: 16, 
                      left: 16, 
                      zIndex: 1,
                      fontWeight: 'bold'
                    }}
                  />
                )}
                {car.status === 'sold' && (
                  <Box sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1
                  }}>
                    <Typography variant="h4" color="white" sx={{ fontWeight: 'bold' }}>
                      ПРОДАДЕНО
                    </Typography>
                  </Box>
                )}
                {car.images && car.images.length > 0 ? (
                  <ImageCarousel images={car.images} startIndex={car.coverPhotoIndex || 0} />
                ) : (
                  <Box sx={{ 
                    height: 300, 
                    background: '#eee', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    borderRadius: 1
                  }}>
                    <Typography>Нема достапни слики</Typography>
                  </Box>
                )}
              </Box>

              {/* Seller Info */}
              <Paper elevation={2} sx={{ 
                mt: 3, 
                p: 2,
                borderRadius: 2,
                backgroundColor: '#f9f9f9'
              }}>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 2,
                  mb: 2
                }}>
                  <Avatar
                    src={car.sellerType === 'dealer' ? car.companyLogo : car.avatar}

                    variant={car.sellerType === 'private' ? 'square' : 'circular'}
                    sx={{ 
                      width: 60, 
                      height: 60,
                      bgcolor: car.sellerType === 'private' ? 'transparent' : '#e0e0e0',
                      fontSize: 24
                    }}
                  >
                    {car.sellerType !== 'private' && sellerName.charAt(0).toUpperCase()}
                  </Avatar>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                      {sellerName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {sellerType}
                    </Typography>
                    {car.city && (
                      <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                        <LocationOn fontSize="small" sx={{ mr: 0.5 }} />
                        {car.city}
                      </Typography>
                    )}
                  </Box>
                </Box>

                {car.sellerType === 'dealer' && (
                  <Box sx={{ mb: 2 }}>
                    <Button 
                      variant="outlined" 
                      color="primary"
                      fullWidth
                      sx={{ py: 1 }}
                      onClick={() => window.location.href = `/${car.dealerSlug}`}

                    >
                      Види ги сите огласи од овој салон
                    </Button>
                  </Box>
                )}

                <Divider sx={{ my: 2 }} />

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {sellerPhone && (
                    <Button 
                      variant="contained" 
                      startIcon={<Phone />}
                      fullWidth
                      href={`tel:${sellerPhone}`}
                      sx={{ py: 1 }}
                    >
                      {sellerPhone}
                    </Button>
                  )}
                  {whatsappNumber && (
                    <Button
                      variant="contained"
                      color="success"
                      startIcon={<WhatsApp />}
                      href={`https://wa.me/${whatsappNumber}`}
                      target="_blank"
                      fullWidth
                      sx={{ py: 1, backgroundColor: '#25D366' }}
                    >
                      WhatsApp
                    </Button>
                  )}
                  {viberNumber && (
                    <Button 
                      variant="contained" 
                      startIcon={
                        <Box sx={{ 
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 24,
                          height: 24,
                          mr: 1
                        }}>
                          <img 
                            src="https://upload.wikimedia.org/wikipedia/commons/7/73/Viber_Logo_2023.svg" 
                            alt="Viber" 
                            style={{ height: '100%', width: 'auto' }} 
                          />
                        </Box>
                      }
                      sx={{ 
                        py: 1,
                        backgroundColor: '#7360F2', 
                        '&:hover': { backgroundColor: '#5a4acf' },
                        display: 'flex',
                        alignItems: 'center'
                      }}
                      href={`viber://chat?number=${viberNumber}`}
                    >
                      Viber
                    </Button>
                  )}
                  {car.userEmail && (
                    <Button
                      variant="outlined"
                      startIcon={<Email />}
                      href={`mailto:${car.userEmail}`}
                      fullWidth
                      sx={{ py: 1 }}
                    >
                      Испрати е-пошта
                    </Button>
                  )}
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="h4" sx={{ 
                fontWeight: 'bold',
                mb: 1,
                color: '#333'
              }}>
                {car.brand} {car.model}
              </Typography>
              
              <Typography variant="h5" sx={{ 
  mb: 3,
  color: '#FF5722',
  fontWeight: 'bold'
}}>
 <Typography variant="h5" sx={{ mb: 3, color: '#FF5722', fontWeight: 'bold' }}>
  {typeof car.dailyPrice === 'number' ? `${car.dailyPrice} €/ден` : `${car.price} €`}
</Typography>

</Typography>

              <Box className="specs-grid" sx={{ 
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr 1fr' : '1fr 1fr 1fr',
                gap: 2,
                mb: 3
              }}>
                <div>
                  <Typography variant="body2" color="text.secondary">Година</Typography>
                  <Typography sx={{ fontWeight: 'medium' }}>{car.year || '—'}</Typography>
                </div>
                <div>
                  <Typography variant="body2" color="text.secondary">Менувач</Typography>
                  <Typography sx={{ fontWeight: 'medium' }}>{car.transmission || '—'}</Typography>
                </div>
                <div>
                  <Typography variant="body2" color="text.secondary">Гориво</Typography>
                  <Typography sx={{ fontWeight: 'medium' }}>{car.fuelType || '—'}</Typography>
                </div>
                <div>
                  <Typography variant="body2" color="text.secondary">Километража</Typography>
                  <Typography sx={{ fontWeight: 'medium' }}>{car.mileage != null ? `${car.mileage} км` : '—'}</Typography>
                </div>
                <div>
                  <Typography variant="body2" color="text.secondary">Боја</Typography>
                  <Typography sx={{ fontWeight: 'medium' }}>{car.color || '—'}</Typography>
                </div>
                <div>
                  <Typography variant="body2" color="text.secondary">Каросерија</Typography>
                  <Typography sx={{ fontWeight: 'medium' }}>{car.carBodyType || '—'}</Typography>
                </div>
                <div>
                  <Typography variant="body2" color="text.secondary">Погон</Typography>
                  <Typography sx={{ fontWeight: 'medium' }}>{car.driveType || '—'}</Typography>
                </div>
                <div>
                  <Typography variant="body2" color="text.secondary">Сила</Typography>
                  <Typography sx={{ fontWeight: 'medium' }}>{car.enginePower != null ? `${car.enginePower} КС` : '—'}</Typography>
                </div>
              </Box>

              {features.length > 0 && (
                <>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>Опрема:</Typography>
                  <Grid container spacing={1} sx={{ mb: 3 }}>
                    {features.map((feature, idx) => (
                      <Grid item xs={6} sm={4} key={idx}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CheckCircle color="success" fontSize="small" />
                          <Typography variant="body2">
                            {featureLabels[feature] || feature}
                          </Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </>
              )}

              {car.description && (
                <>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>Опис:</Typography>
                  <Typography sx={{ 
                    whiteSpace: 'pre-line',
                    lineHeight: 1.6
                  }}>
                    {car.description}
                  </Typography>
                </>
              )}
            </Grid>
          </Grid>

          {/* Mobile Bottom Ad Banner */}
          {isMobile && (
            <Box sx={{ mt: 3 }}>
              <Paper elevation={1} sx={{ p: 1, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Реклама
                </Typography>
                <Box sx={{ 
                  width: '100%', 
                  height: '100px', 
                  backgroundColor: '#f5f5f5',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  Google Ad (320x100)
                </Box>
              </Paper>
            </Box>
          )}
        </Paper>
      </Box>

      {/* Google Ads Sidebar Right - Desktop Only */}
      {!isTablet && (
        <Box className="ad-sidebar right">
          <Paper elevation={1} sx={{ p: 2, height: '100%', textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Реклама
            </Typography>
            <Box sx={{ 
              width: '160px', 
              height: '600px', 
              backgroundColor: '#f5f5f5',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto'
            }}>
              Google Ad (160x600)
            </Box>
          </Paper>
        </Box>
      )}
    </div>
  );
};

export default CarDetails;